import { Capacitor } from "@capacitor/core"
import { Keyboard } from "@capacitor/keyboard"
import { ScreenOrientation } from "@capacitor/screen-orientation"
import { SplashScreen } from "@capacitor/splash-screen"
import { StatusBar, Style } from "@capacitor/status-bar"
import { logger } from "../utils/logger"

/**
 * Native-only UX wiring: status bar theming, soft-keyboard handling and
 * orientation policy. No-ops entirely on the web.
 */

// Keep in sync with --surface in src/style.css — the status bar should read
// as an extension of the app header, not a differently-colored strip.
const SURFACE_LIGHT = "#ffffff"
const SURFACE_DARK = "#1f1b18"

async function syncStatusBarWithTheme(): Promise<void> {
  const isDark = document.documentElement.getAttribute("data-theme") === "dark"
  try {
    // Style.Dark = light text (for dark backgrounds), Style.Light = dark text.
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: isDark ? SURFACE_DARK : SURFACE_LIGHT })
    }
  } catch (error) {
    logger.warn("Failed to sync status bar with theme", { error })
  }
}

// Hard ceiling on how long the launch splash may stay up. The splash is now
// dismissed by the app itself (capacitor.config sets launchAutoHide: false) so
// that a slow cold start shows the brand mark instead of a blank webview — but
// that also means a startup path that never reaches hideLaunchSplash would
// leave the user staring at it. This timer guarantees it always comes down.
const SPLASH_FAILSAFE_MS = 6000

let splashHidden = false

async function hideSplashNow(): Promise<void> {
  if (splashHidden) return
  splashHidden = true
  if (!Capacitor.isNativePlatform()) return
  try {
    await SplashScreen.hide()
  } catch (error) {
    logger.warn("Failed to hide the splash screen", { error })
  }
  // The splash window owns the status-bar appearance while it is up, so the
  // style initNativeUx set is thrown away when it comes down — leaving light
  // icons over the light header, i.e. an unreadable clock. Re-assert it now
  // that the app's own window is on screen. (Warm starts looked fine, which is
  // why this only shows after a cold launch.)
  await syncStatusBarWithTheme()
}

/**
 * Dismiss the native launch splash. Safe to call repeatedly and on the web,
 * where it does nothing. Call it as soon as the app has something real to
 * show — the failsafe below is a backstop, not the intended path.
 */
export function hideLaunchSplash(): Promise<void> {
  return hideSplashNow()
}

if (Capacitor.isNativePlatform()) {
  setTimeout(() => {
    void hideSplashNow()
  }, SPLASH_FAILSAFE_MS)
}

// Screens whose shorter CSS dimension is below this are treated as phones.
// Matches Android's conventional 600dp phone/tablet boundary.
const PHONE_MAX_SHORT_SIDE_PX = 600

function isPhone(): boolean {
  return Math.min(window.screen.width, window.screen.height) < PHONE_MAX_SHORT_SIDE_PX
}

export async function initNativeUx(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  // Match the status bar to the current theme now, and re-sync whenever the
  // theme toggle flips data-theme on <html>.
  await syncStatusBarWithTheme()
  const themeObserver = new MutationObserver(() => {
    void syncStatusBarWithTheme()
  })
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })

  // Phones lock to portrait (standard for finance apps); tablets keep free
  // rotation so split/landscape use still works.
  if (isPhone()) {
    try {
      await ScreenOrientation.lock({ orientation: "portrait" })
    } catch (error) {
      logger.warn("Failed to lock screen orientation", { error })
    }
  }

  // Keyboard handling: expose the keyboard height as a CSS variable and a
  // body class so bottom sheets / modal action rows can pad themselves clear
  // of the keyboard, and keep the focused field visible.
  try {
    await Keyboard.addListener("keyboardWillShow", (info) => {
      document.documentElement.style.setProperty("--keyboard-height", `${info.keyboardHeight}px`)
      document.body.classList.add("keyboard-open")
    })
    await Keyboard.addListener("keyboardDidShow", () => {
      const active = document.activeElement
      if (active instanceof HTMLElement) {
        active.scrollIntoView({ block: "center", behavior: "smooth" })
      }
    })
    await Keyboard.addListener("keyboardWillHide", () => {
      document.documentElement.style.setProperty("--keyboard-height", "0px")
      document.body.classList.remove("keyboard-open")
    })
  } catch (error) {
    logger.warn("Failed to install keyboard listeners", { error })
  }
}
